import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { Image } from '@codes/ui-kit/components/ui/image';
import { ChevronsLeft, Menu } from 'lucide-react';

import { CodesLogoImage } from '@codes/assets';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
    Button,
    Sheet,
    SheetClose,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
    cn,
} from '@codes/ui-kit';

import { MenuItem } from '../../consts';

interface MobileSideBarProps {
    items: MenuItem[];
}

export const MobileSideBar = ({ items }: MobileSideBarProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    const isCurrentPath = (url: string) => {
        if (!url) {
            return false;
        }
        return location.pathname.includes(url);
    };

    const isAnySubPathCurrent = (item: MenuItem) => {
        if (item.href && isCurrentPath(item.href)) {
            return true;
        }
        if (item.subItems) {
            return item.subItems.some(sub => isCurrentPath(sub.href));
        }
        return false;
    };

    const navigateButtonTapped = (hasDropdown?: boolean, url?: string) => {
        if (hasDropdown || !url) {
            return;
        }
        navigate(url);
    };

    return (
        <Sheet>
            <SheetTrigger asChild>
                <Button variant="ghost" className="p-1 h-auto">
                    <Menu size={18} />
                </Button>
            </SheetTrigger>
            <SheetContent side="left" className="md:hidden py-4 px-3 w-[260px] bg-card/95 backdrop-blur-md">
                <SheetHeader className="pt-0 h-10 border-none flex items-center justify-between flex-row px-1">
                    <SheetTitle className="flex items-center">
                        <Image src={CodesLogoImage} className="h-[18px]" />
                    </SheetTitle>
                    <SheetClose asChild>
                        <Button variant="ghost" className="p-1 h-auto">
                            <ChevronsLeft size={16} className="text-text-400" />
                        </Button>
                    </SheetClose>
                </SheetHeader>

                <div className="h-px bg-border/40 my-2" />

                <Accordion type="single" collapsible className="w-full flex flex-col gap-0.5">
                    {items.map((item, index) => (
                        <AccordionItem key={index} value={`item-${index}`} className="border-none">
                            <AccordionTrigger
                                className={cn(
                                    'flex justify-between !p-2.5 text-sm rounded-lg transition-colors duration-150',
                                    item.hasDropdown ? '' : '[&>svg:last-child]:hidden',
                                    isAnySubPathCurrent(item)
                                        ? 'bg-accent text-point font-medium'
                                        : 'text-text-600 hover:bg-hover/60',
                                    'data-[state=open]:!rounded-b-none'
                                )}
                                onClick={() => navigateButtonTapped(item.hasDropdown, item.href)}
                            >
                                <div className="flex items-center gap-2.5">
                                    {item.icon && (
                                        <div
                                            className={cn(
                                                'shrink-0',
                                                isAnySubPathCurrent(item) ? 'opacity-100' : 'opacity-50'
                                            )}
                                        >
                                            <item.icon className="w-[18px] h-[18px] !rotate-0" strokeWidth={1.75} />
                                        </div>
                                    )}
                                    <span className="text-[13px]">{t(item.label)}</span>
                                </div>
                            </AccordionTrigger>
                            {item.hasDropdown && (
                                <AccordionContent className="!py-1.5 bg-hover/40 rounded-b-lg">
                                    {item.subItems?.map((subItem, subIndex) => (
                                        <button
                                            onClick={() => navigateButtonTapped(false, subItem.href)}
                                            key={subIndex}
                                            className={cn(
                                                'w-full text-left px-4 py-2 rounded-md text-[13px] font-medium transition-colors duration-100',
                                                isAnySubPathCurrent(subItem)
                                                    ? 'text-point'
                                                    : 'text-text-400 hover:text-text-600'
                                            )}
                                        >
                                            {t(subItem.label)}
                                        </button>
                                    ))}
                                </AccordionContent>
                            )}
                        </AccordionItem>
                    ))}
                </Accordion>
            </SheetContent>
        </Sheet>
    );
};
